import React, { useState } from 'react';

import { SureYesNoButton } from '../../../ClickOnceButton';
import { strangeCompare } from '../../../utils/compareUtils';
import logger from '../../../utils/logger';
import { SmallButton } from '../Button';

/**
 * Props for OpenCloseButton component
 */
interface OpenCloseButtonProps {
  /** CSS position styles */
  position: React.CSSProperties;
  /** Whether the list is currently open */
  open: boolean;
  /** Function to toggle open state */
  setOpen: (open: boolean) => void;
  /** Text to display when closed */
  text: string;
  /** Whether positioned on left side */
  left?: boolean;
  /** Icon to display when closed */
  icon?: string;
}

/**
 * OpenCloseButton Component - Toggle button for opening/closing lists
 *
 * Displays an icon and text in a compact button format.
 * Used to toggle the visibility of List components.
 */
const OpenCloseButton: React.FC<OpenCloseButtonProps> = ({
  position,
  open,
  setOpen,
  text,
  left: _left,
  icon,
}) => {
  const buttonStyle: React.CSSProperties = {
    borderColor: '#0004',
    borderWidth: '0.15vmin',
    borderStyle: 'solid',
    borderRadius: '0.4vmin',
    background: '#fff8',
    color: '#000',
    fontSize: '1.8vmin',
    padding: '0.5vmin',
    margin: '0.1vmin',
    cursor: 'pointer',
  };

  return (
    <div
      style={{
        ...buttonStyle,
        ...position,
        minWidth: '4vmin',
        width: '4vmin',
        height: '4vmin',
        background: '#fff8',
        borderStyle: 'solid',
        textAlign: 'center',
        borderColor: '#eee4',
        fontWeight: 800,
        fontSize: '2.8vmin',
        zIndex: 1650, // Higher than list content
      }}
      onClick={() => setOpen(!open)}
    >
      {open ? (
        <div style={{ minWidth: '4vmin' }}>
          ⊝<br />
          <div style={{ fontSize: '1.1vmin' }}>Close</div>
        </div>
      ) : (
        <div style={{ minWidth: '4vmin' }}>
          {icon ? icon : '⊞'}
          <br />
          <div style={{ fontSize: '1.1vmin' }}>{text}</div>
        </div>
      )}
    </div>
  );
};

/**
 * Generic item type for list - must have an id
 */
export interface ListItem {
  id: string | number;
}

/**
 * Template for adding new items
 */
export interface AddTemplateItem extends ListItem {
  id: string;
  name: string;
  value: string;
}

/**
 * Formatted item for display — values may be strings, numbers, or React nodes
 * (e.g. a status dot with a hover tooltip).
 *
 * When a column contains a React node, sorting by that column falls back to
 * insertion order (sort key is treated as empty string). String/number values
 * sort normally via strangeCompare.
 */
export type FormattedItem = Record<string, string | number | React.ReactNode>;

/**
 * Editable fields map - key is field name, value is edit handler
 */
export type EditableFields<T extends ListItem> = {
  [key: string]: (item: T, value: string) => void;
};

/**
 * Edited fields state - maps item id to edited values
 */
export type EditedFields = {
  [itemId: string | number]: string | Record<string, string | undefined>;
};

/**
 * Props for the List component
 */
export interface ListProps<T extends ListItem = ListItem> {
  /** Position on left side of screen */
  left?: boolean;
  /** Position lower on screen (for stacking) */
  low?: boolean;
  /** Array of data items to display */
  items: T[];
  /** Function to format items for display */
  format: (item: T) => FormattedItem;
  /** Column headers */
  headers?: string[];
  /** Function called when item is selected */
  select: (item: T | null) => void;
  /** Function to check if item is selected */
  isSelected: (item: T) => boolean;
  /** Display text for the list */
  text: string;
  /** Map of editable field handlers */
  editableFields?: EditableFields<T>;
  /** Current edited field values */
  editedFields?: EditedFields;
  /** Handler for list close */
  onClose?: () => void;
  /** Handler for submitting edits */
  onSubmitEdits?: () => void | Promise<void>;
  /** Handler for canceling edits */
  onCancelEdits?: () => void;
  /** Icon to display when collapsed */
  icon?: string;
  /** Handler for adding new items */
  onAdd?: (values: Record<string, string>) => void | Promise<void>;
  /** Handler for deleting items */
  onDelete?: (itemId: string | number) => void | Promise<void>;
  /** Template for new item creation */
  addTemplate?: AddTemplateItem[];
  /** Initial open state */
  open?: boolean;
  /** Initial sort direction */
  sortReversed?: boolean;
  /** List name (used for add dialog) */
  name?: string;
}

/**
 * List Component - Interactive list with sorting, editing, and filtering
 *
 * A comprehensive list component that supports:
 * - Sorting by column headers
 * - Inline editing of fields
 * - Adding and deleting items
 * - Item selection
 * - Scrolling for large datasets
 * - Customizable positioning and formatting
 * - React node values in format functions (e.g. status dots with tooltips)
 *
 * @example
 * <List
 *   items={users}
 *   format={(user) => ({ name: user.name, email: user.email })}
 *   headers={['Name', 'Email']}
 *   select={setSelectedUser}
 *   isSelected={(user) => user.id === selectedId}
 *   text="Users"
 * />
 */
export const List = <T extends ListItem>(props: ListProps<T>): React.ReactElement => {
  const {
    left,
    low,
    items,
    format,
    headers,
    select,
    isSelected,
    text,
    editableFields,
    editedFields,
    onClose,
    onSubmitEdits,
    onCancelEdits,
    icon,
    onAdd,
    onDelete,
    addTemplate,
  } = props;

  const [open, setOpen2] = useState(props.open ? true : false);
  const [scale, setScale] = useState(100);
  const [sortBy, setSortBy] = useState(0);
  const [sortReversed, setSortReversed] = useState(props.sortReversed ? true : false);
  const [adding, setAdding] = useState<boolean>(false);
  const [addEdits, setAddEdits] = useState<Record<string, string>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<string | number | null>(null);
  const [scroll, _setScroll] = useState(0);
  const [touchY, setTouchY] = useState(0);

  const setOpen = (value: boolean) => {
    if (value === true) {
      setScale(100);
      setScroll(0);
    } else {
      if (onClose) onClose();
    }
    setOpen2(value);
  };

  // Layout configuration
  const top = low ? '8vh' : '1vh';
  const topadd = low ? '16vh' : '9vh';
  const hPosition: React.CSSProperties = left
    ? { left: '1vmin', display: 'flex' }
    : { right: '1vmin', display: 'flex', flexDirection: 'row-reverse' };
  const position: React.CSSProperties = {
    ...hPosition,
    position: 'fixed',
    top,
    bottom: '20vh',
    background: '#44c4',
    borderRadius: '1vmin',
    zIndex: 1600,
  };

  const itemHeightVh = 3.6;
  const heightVh = 70;
  const itemCount = (heightVh - 10) / itemHeightVh;

  /**
   * Set scroll position with bounds checking
   */
  const setScroll = (y: number) => {
    if (y < 0) {
      _setScroll(0);
    } else if (items) {
      if (y > items.length - itemCount) {
        _setScroll(items.length - itemCount);
      } else {
        _setScroll(y);
      }
    } else {
      _setScroll(0);
    }
  };

  // Show collapsed state
  if (!open) {
    return (
      <OpenCloseButton
        position={position}
        open={open}
        setOpen={setOpen}
        text={text}
        left={left}
        icon={icon}
      />
    );
  }

  // Delete confirmation dialog
  let deleteConfirmButton: React.ReactNode = null;
  if (deleteConfirm) {
    deleteConfirmButton = (
      <SureYesNoButton
        open={true}
        noText={true}
        key="delete-confirm"
        text="Delete"
        onClick={() => {
          if (onDelete) onDelete(deleteConfirm);
          setDeleteConfirm(null);
        }}
        onNo={() => setDeleteConfirm(null)}
      />
    );
  }

  /**
   * Format individual list item for display
   */
  const formatItem = (item: T, formatted: FormattedItem, ix: number): React.ReactNode => {
    const itemWidth = 99 / Object.keys(formatted).length + '%';
    return (
      <div
        key={ix}
        data-testid={`list-item-${String(item.id)}`}
        style={{
          background: isSelected(item) ? '#bbf' : '#fff',
          borderBottom: '1px solid #ddd',
          // minHeight (not height) allows rows to expand when text wraps — important for readability on iPad.
          minHeight: itemHeightVh + 'vh',
          width: '98%',
          margin: 1,
          // max() ensures readable font size on iPad where vmin units can resolve too small.
          fontSize: 'max(12px, 1.8vmin)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          opacity: '70%',
        }}
        onClick={(e) => {
          e.stopPropagation();
          isSelected(item) ? select(null) : select(item);
        }}
      >
        {Object.keys(formatted).map((k, i) => (
          <div
            key={i}
            style={{
              width: itemWidth,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              padding: '0.3vmin 0.5vmin 0.3vmin 0',
            }}
          >
            {editableFields && editableFields.hasOwnProperty(k) ? (
              <input
                style={{ fontSize: '1.8vmin' }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onChange={(e) => editableFields[k](item, e.target.value)}
                value={
                  editedFields && editedFields.hasOwnProperty(item.id)
                    ? typeof editedFields[item.id] === 'object'
                      ? (editedFields[item.id] as Record<string, string>)[k]
                      : (editedFields[item.id] as string)
                    : String(formatted[k] ?? '')
                }
              />
            ) : (
              formatted[k]
            )}
          </div>
        ))}
        {onDelete ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirm(item.id);
            }}
            key="delete"
            style={{ fontWeight: 600, width: '3vmin' }}
          >
            ⊝
          </div>
        ) : null}
      </div>
    );
  };

  /**
   * Format table headers with sorting controls
   */
  const formatHeader = (headers: string[]): React.ReactNode => {
    const itemWidth = 99 / headers.length + '%';
    return (
      <div
        key="header"
        style={{
          background: 'white',
          height: '2vh',
          width: '98%',
          margin: 1,
          fontSize: '1.8vmin',
          fontWeight: 600,
          display: 'flex',
          flexDirection: 'row',
          borderBottomStyle: 'solid',
          borderBottomColor: '#ccc8',
        }}
        onClick={(e) => {
          e.stopPropagation();
          select(null);
        }}
      >
        {headers.map((k, i) => (
          <div
            key={i}
            role="columnheader"
            tabIndex={0}
            style={{ width: itemWidth, fontWeight: sortBy === i ? 600 : 300 }}
            onClick={(e) => {
              e.stopPropagation();
              if (sortBy !== i) setSortBy(i);
              else setSortReversed(!sortReversed);
            }}
          >
            {k} {sortBy !== i ? '' : sortReversed ? '▼' : '▲'}
          </div>
        ))}
        {onDelete ? <div key="delete-header" style={{ width: '3vmin' }} /> : null}
      </div>
    );
  };

  /**
   * Generate default values for new item creation
   */
  const generateDefault = (): Record<string, string> => {
    if (!addTemplate) return {};
    const obj: Record<string, string> = {};
    for (let i = 0; i < addTemplate.length; ++i) {
      obj[addTemplate[i].id] = addTemplate[i].value;
    }
    return obj;
  };

  // Handle adding new items
  if (adding) {
    let addItems: AddTemplateItem[] = [];
    if (addTemplate) {
      addItems = addTemplate;
    } else if (headers) {
      // Generate a template dynamically
      for (let i = 0; i < headers.length; ++i) {
        const obj: AddTemplateItem = { id: headers[i], name: headers[i], value: '' };
        addItems.push(obj);
      }
    }

    const formatAddItem = (item: AddTemplateItem): FormattedItem => ({
      name: item.name,
      value: item.value,
    });

    const onEditValue = (obj: AddTemplateItem, value: string) => {
      logger.debug('onEditValue', obj.id, value);
      logger.debug('previous:', addEdits);
      const copy = JSON.parse(JSON.stringify(addEdits)) as Record<string, string>;
      copy[obj.id] = value;
      setAddEdits(copy);
      logger.debug('next:', copy);
    };

    return (
      <List<AddTemplateItem>
        key="add-list"
        left={left}
        low={low}
        name="Add"
        open={true}
        text={text}
        icon="+"
        items={addItems}
        format={formatAddItem}
        isSelected={() => false}
        select={() => {}}
        editableFields={{ value: onEditValue }}
        editedFields={addEdits}
        onSubmitEdits={() => {
          if (onAdd) onAdd(addEdits);
          setAddEdits({});
          setAdding(false);
        }}
        onCancelEdits={() => setAddEdits({})}
        onClose={() => setAdding(false)}
      />
    );
  }

  // Pair each item with its formatted representation so we can sort without
  // losing the reference to the original item. This replaces the previous
  // JSON.stringify-keyed lookup, which broke when format() returned React nodes
  // (functions and Symbols are not serialised by JSON.stringify).
  type FormattedWithItem = { formatted: FormattedItem; item: T };
  const formattedWithItems: FormattedWithItem[] = [];
  if (Array.isArray(items)) {
    items.forEach((item) => {
      formattedWithItems.push({ formatted: format(item), item });
    });
  }

  const keys = formattedWithItems.length > 0 ? Object.keys(formattedWithItems[0].formatted) : [];

  // React nodes (e.g. status dots) are not comparable as strings — fall back to
  // empty string so those columns retain insertion order when sorted.
  const getSortKey = (value: FormattedItem[string]): string => {
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    return '';
  };

  formattedWithItems.sort((a, b) => {
    let result = strangeCompare(
      getSortKey(a.formatted[keys[sortBy]]),
      getSortKey(b.formatted[keys[sortBy]])
    );
    if (sortReversed) result = -result;
    return result;
  });

  // Calculate scroll bar dimensions
  const visibleVh =
    items.length > itemCount ? ((heightVh - 3) * itemCount) / items.length : heightVh - 3;
  const scrollVh = ((heightVh - 6) * scroll) / items.length;
  const scrollTopMargin = 3 + scrollVh;
  const scrollHeight = visibleVh;

  return (
    <div
      style={{
        ...position,
        width: '85vw',
        minHeight: heightVh + 'vh',
        maxHeight: heightVh + 'vh',
        touchAction: 'none',
      }}
      onWheel={(e) => {
        setScroll(scroll + e.deltaY / 20);
      }}
      onTouchStart={(e) => {
        setTouchY(e.touches[e.touches.length - 1].screenY);
      }}
      onTouchMove={(e) => {
        logger.debug('tl: ' + e.touches.length);
        setScroll(scroll - (e.touches[e.touches.length - 1].screenY - touchY) / 10);
        setTouchY(e.touches[e.touches.length - 1].screenY);
      }}
      onClick={() => setOpen(false)}
    >
      <OpenCloseButton position={position} open={open} setOpen={setOpen} text={text} left={left} />
      {onAdd ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setAdding(true);
            setAddEdits(generateDefault());
          }}
          style={{
            ...position,
            top: topadd,
            padding: '2vw',
            height: '3vmin',
            alignContent: 'center',
            fontSize: '3vmin',
            background: '#0000',
            minHeight: '20vh',
          }}
        >
          +
        </div>
      ) : null}
      <div style={{ width: '9vmin', background: '#ccc3' }} />
      <div
        style={{
          scale: scale + '%',
          margin: '1%',
          width: '83vw',
          minHeight: heightVh - 2 + 'vh',
          maxHeight: heightVh - 2 + 'vh',
          background: '#eef8',
          textAlign: 'left',
          pointerEvents: 'all',
        }}
      >
        {editedFields && Object.keys(editedFields).length > 0 && (
          <div>
            <SmallButton text="Submit Edits" onClick={() => onSubmitEdits && onSubmitEdits()} />
            <SmallButton text="Cancel" onClick={() => onCancelEdits && onCancelEdits()} />
          </div>
        )}
        {headers && formatHeader(headers)}
        {formattedWithItems.map(({ formatted: formattedItem, item }, ix) =>
          ix >= scroll && ix < scroll + itemCount ? formatItem(item, formattedItem, ix) : null
        )}
      </div>
      {formattedWithItems.length > itemCount ? (
        <div
          style={{
            borderStyle: 'solid',
            borderColor: '#fff8',
            background: '#55c4',
            width: '5px',
            marginRight: '0.2vmin',
            marginTop: scrollTopMargin + 'vh',
            height: scrollHeight + 'vh',
            borderRadius: '1vw',
          }}
        />
      ) : null}
      {deleteConfirmButton}
    </div>
  );
};

export default List;
